import objectFitImages from 'object-fit-images';
import ImageBuddy from 'imagebuddy';

import ThemeWelcome from './components/testing/theme-welcome';
import SiteHeader from './components/site-header';
import BannerVideo from './components/banner-video';
import BannerSlider from './components/banner-slider';
import GridSlider from './components/grid-slider';

import Modal from './components/modal';

import PostsListBlog from './components/posts-list/posts-list-blog';
import PostsListSearch from './components/posts-list/posts-list-search';

import { processExternalLinks } from './util/general-util';
import ScrollWatcher from './util/scroll-watcher';
import SiteEvents, { SiteEventNames } from './util/site-events';
import { loadIntersectionObserver } from './util/load-dependencies';
import { createSingleUseObserver } from './util/intersection-observer';
import { initHelloBar } from './util/hello-bar';
import ModalDefault from './components/modal-default';
import EqualHeight from './util/equal-height';

import LogosSlider from './components/logos-silder';

import TestimonialsSlider from './components/testimonials-slider';
import LinkCardsSlider from './components/link-cards-slider';
import StatsSlider from './components/stats-slider';
import SocialShare from './components/social-share';
import Accordion from './components/accordion';
import SideTabs from './components/side-tabs';
import SideBySide from './components/side-by-side';
import NewsletterSubmit from './components/newsletter-submit';
import MediaContentSlider from './components/media-content-slider';
import MediaContentVideo from './components/media-content-video';

import ImageGallerySlider from './components/image-gallery-slider';
import ImageGalleryParallax from './components/image-gallery-parallax';


import AccordionImage from './blocks/accordion-image';

import ResourceTemplate from './templates/resource';
import EventTemplate from './templates/event';
import LodgeTabs from './components/lodge-tabs';
import DestinationTabs from './components/destination-tabs';

import BannerNextButton from './components/banner-next-button';
import LoadMoreLinkCards from './components/lode-more-link-cards';

class App {
	constructor() {
		this.instances = {
			components: {
				accordion: [],
				bannerVideo: null,
				equalHeight: [],
				imageGallerySlider: [],
				linkCardsSlider: [],
				logosSlider: [],
				mediaContentSlider: [],
				modal: [],
				modalVideoPopup: [],
				newsletterSubmit: [],
				postsListBlog: [],
				postsListSearch: [],
				siteHeader: null,
				socialShare: null,
				statsSlider: [],
				testimonialsSlider: [],
				themeWelcome: null,
				bannerNextButton: [], 
			},
			templates: {
				resourceTemplate: null,
				eventTemplate: null
			},
			blocks: {
				accordionImage: [],
				sideTabs: [],
				sideBySide: [],

			},
			scrollWatcher: null,
			imageBuddy: null
		};

		this.init();
		this.initComponents();
		this.initTemplates();
		this.initBlocks();
		this.initSocialShare();
	}

	init() {
		processExternalLinks({
			target: '_blank',
			rel: 'noopener'
		});

		Modal.setDefaults({
			closeDuration: 400,
			closeButtonContent: `
				<svg xmlns="http://www.w3.org/2000/svg" class="modal__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			`.trim()
		});

		this.scrollWatcher = new ScrollWatcher((params) => {
			ScrollWatcher.defaultCallback(params, 100);
		});

		// initialize ImageBuddy and setup events
		this.instances.imageBuddy = new ImageBuddy({
			lazyLoad: true
			// debug: true
		});

		ImageBuddy.on('image-loaded', (imgEl) => {
			objectFitImages(imgEl);
		});

		SiteEvents.subscribe(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE, (opts) => {
			this.instances.imageBuddy.update(opts || {});
		});

		initHelloBar((hbEl) => {
			document.body.style.marginTop = `${hbEl.offsetHeight}px`;

			const siteHeaderEl = document.querySelector('.site-header');

			if (siteHeaderEl) {
				siteHeaderEl.style.marginTop = `${hbEl.offsetHeight}px`;
			}
		});

		// ObjectFitImages: only act upon images that have a valid src attribute
		// Note: use the 'object-fit-polyfill' SCSS mixin to create the necessary 'font-family' attribute
		objectFitImages('img:not([src^="data:"])');
	}

	async initComponents() {
		// theme welcome
		// TODO: remove during development
		const themeWelcomeEl = document.querySelector('.theme-welcome');

		if (themeWelcomeEl) {
			this.instances.themeWelcome = new ThemeWelcome(themeWelcomeEl);
		}

		// site header
		const siteHeaderEl = document.querySelector('.site-header');

		if (siteHeaderEl) {
			this.instances.components.siteHeader = new SiteHeader(siteHeaderEl);
		}

		// newsletter signup
		Array.from(document.querySelectorAll('.newsletter-signup')).forEach((newsletterSignupEl) => {
			this.instances.components.newsletterSubmit.push(new NewsletterSubmit(newsletterSignupEl));
		});
	
		//	this.initAjaxLoadMoreFilters();

		// banner cover video
		const bannerCoverVideoEl = document.querySelector('.banner__cover-video');

		if (bannerCoverVideoEl) {
			this.instances.components.bannerVideo = new BannerVideo(bannerCoverVideoEl);
		}

		const bannerSliderEl = document.querySelector('.banner-default__slider');

		if (bannerSliderEl) {
			this.instances.components.bannerSlider = new BannerSlider();
		}

		// grid slider
		const gridSliderEls = document.querySelectorAll('.block-grid__slider-wrapper');

		if (gridSliderEls.length) {
			gridSliderEls.forEach((wrapper) => {
				new GridSlider(wrapper);
			});
		} 

		// posts list - blog
		Array.from(document.querySelectorAll('.posts-list--blog')).forEach((postsListBlogEl) => {
			this.instances.components.postsListBlog.push(new PostsListBlog(postsListBlogEl));
		});

		// posts list - search
		Array.from(document.querySelectorAll('.posts-list--search')).forEach(
			(postsListSearchEl) => {
				this.instances.components.postsListSearch.push(
					new PostsListSearch(postsListSearchEl)
				);
			}
		);

		// Default Modal
		Array.from(document.querySelectorAll('.js__modal--default')).forEach(
			(modalDefaultEl) => {
				this.instances.components.modal.push(new ModalDefault(modalDefaultEl));
			}
		);

		// Testimonials Slider
		Array.from(document.querySelectorAll('.block-testimonials')).forEach(

			(testimonialsBlock) => {
				this.slider = testimonialsBlock.querySelector('.block-testimonials__testimonials-slider');
				this.prevButton = testimonialsBlock.querySelector('.block-testimonials__custom-prev-button');
				this.nextButton = testimonialsBlock.querySelector('.block-testimonials__custom-next-button');
				if (this.slider) {
					this.instances.components.testimonialsSlider.push(
						new TestimonialsSlider(this.slider, this.prevButton, this.nextButton)
					);
				}
			}
		);

		// Link Cards Slider
		Array.from(document.querySelectorAll('.block-link-cards--carousel')).forEach(
			(linkCardsBlock) => {
				this.slider = linkCardsBlock.querySelector('.block-link-cards__carousel-container');
				this.prevButton = linkCardsBlock.querySelector('.block-link-cards__carousel-prev');
				this.nextButton = linkCardsBlock.querySelector('.block-link-cards__carousel-next');
				if (this.slider && this.prevButton && this.nextButton) {
					this.instances.components.linkCardsSlider.push(
						new LinkCardsSlider(this.slider, this.prevButton, this.nextButton)
					);
				}
			}
		);

		// Image Gallery Slider
		Array.from(document.querySelectorAll('.block-image-gallery')).forEach(

			(imageGalleryBlock) => {
				this.slider = imageGalleryBlock.querySelector('.block-image-gallery__slider');
				this.prevButton = imageGalleryBlock.querySelector('.custom-prev-button');
				this.nextButton = imageGalleryBlock.querySelector('.custom-next-button');
				this.nav = imageGalleryBlock.querySelector('.custom-nav-container-image-gallery');
				if (this.slider) {
					this.instances.components.imageGallerySlider.push(
						new ImageGallerySlider(this.slider, this.prevButton, this.nextButton, this.nav)
					);
				}
			}
		);

		//Image Gallery Parallax
		Array.from(document.querySelectorAll('.block-image-gallery')).forEach((galleryBlock) => {
			const parallaxBlock = galleryBlock.querySelector('.image-gallery__full-width-edge-parallax');
			if (parallaxBlock) {
			  new ImageGalleryParallax(parallaxBlock);
			}
		  });

		// Logos Slider
		Array.from(document.querySelectorAll('.block-logos')).forEach(

			(logosBlock) => {
				this.slider = logosBlock.querySelector('.block-logos__logos-slider');
				if (this.slider) {
					this.instances.components.logosSlider.push(
						new LogosSlider(this.slider)
					);
				}
			}
		);

		// Media Content Slider
		Array.from(document.querySelectorAll('.block-media-content__slider-wrapper')).forEach(

			(mediaContentSlider) => {
				this.slider = mediaContentSlider.querySelector('.block-media-content__slider');
				this.instances.components.mediaContentSlider.push(
					new MediaContentSlider(this.slider)
				);
			}
		);

		//Lodge Tabs
		Array.from(document.querySelectorAll('.lodge-tabs')).forEach((tabsBlock) => {
			new LodgeTabs(tabsBlock); 
		});

		//Destination Tabs
		Array.from(document.querySelectorAll('.destination-tabs')).forEach((tabsBlock) => {
			new DestinationTabs(tabsBlock); 
		});

		// Media Player Video
		const ytPlayers = document.querySelectorAll('.custom-youtube-player');
		if (ytPlayers.length > 0) {
			const videoPlayer = new MediaContentVideo();
			videoPlayer.init();
		}

		// Social Share
		const socialShareEl = document.querySelector('.tmpl-single');

		if (socialShareEl) {
			this.instances.components.socialShare = new SocialShare(socialShareEl);
		}

		// accordion
		Array.from(document.querySelectorAll('.accordion-container')).forEach((accordionEl) => {
			this.instances.components.accordion.push(new Accordion(accordionEl));
		});

		// Stats Slider
		Array.from(document.querySelectorAll('.block-stats')).forEach(
			(statsBlock) => {
				this.slider = statsBlock.querySelector('.stats__stat-slider');
				this.nav = statsBlock.querySelector('.custom-nav-container-stats');
				if (this.slider) {
					this.instances.components.statsSlider.push(
						new StatsSlider(this.slider, this.nav)
					);
				}
			}
		);

		// Equalize Height
		Array.from(document.querySelectorAll('.equal-height')).forEach(

			(equalHeightContainer) => {

				this.instances.components.equalHeight.push(
					new EqualHeight(equalHeightContainer)
				);
			}
		);

		// Banner next button
		Array.from(document.querySelectorAll('.banner-default')).forEach((bannerBlock) => {
			const nextButton = bannerBlock.querySelector('.banner-default__next');

			if (nextButton) {
				this.instances.components.bannerNextButton.push(
					new BannerNextButton(bannerBlock, nextButton)
				);
			}
		});
		
		// Link Cards (Load More) — only run on the inner block (.block-inner.block-link-cards), never on the plugin's outer wrapper
		Array.from(document.querySelectorAll('.block-inner.block-link-cards')).forEach((block) => {
			if (!block.classList.contains('block-link-cards--carousel')) {
				new LoadMoreLinkCards(block);
			}
		});

	}

	/* initAjaxLoadMoreFilters() {
		document.addEventListener('almComplete', function(e) {
			if (typeof window.AjaxLoadMoreFilters !== 'undefined') {
				window.AjaxLoadMoreFilters.init();
			}
		});
	
		document.body.addEventListener('click', function(e) {
			if (e.target.matches('.alm-filter--text-wrap.has-button button')) {
				e.preventDefault();
				const container = e.target.closest('.alm-filters');
				if (container) {
					const input = container.querySelector('input.alm-filter--textfield');
					if (input) {
						input.dispatchEvent(new Event('change', { bubbles: true }));
					}
				}
			}
		});
	} */
	

	initTemplates() {

		// banner cover video
		const resourceTemplateEl = document.querySelector('.post-type-archive-resource');

		if (resourceTemplateEl) {
			this.instances.templates.resourceTemplate = new ResourceTemplate(resourceTemplateEl);
		}

		const eventTemplateEl = document.querySelector('.post-type-archive-event');

		if (eventTemplateEl) {
			this.instances.templates.eventTemplate = new EventTemplate(eventTemplateEl);
		}

	}

	async initBlocks() {

		// Accordion Image
		Array.from(document.querySelectorAll('.block-accordion')).forEach(
			(accordionImage) => {
				this.instances.blocks.accordionImage.push(
					new AccordionImage(accordionImage)
				);
			}
		);

		// Side Tabs
		Array.from(document.querySelectorAll('.block-side-tabs')).forEach(
			(sideTabs) => {
				this.instances.blocks.sideTabs.push(
					new SideTabs(sideTabs)
				);
			}
		);

		// Side by Side
		Array.from(document.querySelectorAll('.block-side-by-side')).forEach(
			(sideBySide) => {
				this.instances.blocks.sideBySide.push(
					new SideBySide(sideBySide)
				);
			}
		);

		// setup block animation watchers
		const animateBlocks = document.querySelectorAll('.block-animate');

		if (animateBlocks && animateBlocks.length) {
			try {
				await loadIntersectionObserver();

				animateBlocks.forEach((watchEl) =>
					createSingleUseObserver(watchEl, (entry, el) => {
						el.classList.remove('block-animate');
					})
				);
			} catch (err) {
				console.error(err);
			}
		}
	}

	initSocialShare() {
		const socialShareEls = document.querySelectorAll('[data-social-share]');

		Array.from(socialShareEls).forEach((el) => {
			el.addEventListener('click', (e) => {
				const site = el.getAttribute('data-social-share');
				const shareUrl = el.getAttribute('href');

				e.preventDefault();

				if (!shareUrl) {
					return;
				}

				window.open(shareUrl, `${site}Share`, 'width=626,height=436');
			});
		});
	}
}

export default App;
